<template>
  <div
    :ref="setEspotRef"
    :class="{ 'h-full': fullHeight }"
    :data-cm-metadata="placementMetadata"
    :data-test-id="`cms-${section.name}`"
  >
    <base-wrapper
      :active="!!highlight"
      class="relative min-h-10 b-2"
      :style="highlight ? `border-color: ${highlightBorderColor}` : undefined"
    >
      <span
        v-if="highlight"
        class="absolute left-0 top-0 z-1 b-b b-r p-1 text-sm fw-bold"
        :style="{
          borderColor: highlightBorderColor,
          backgroundColor: highlightBackgroundColor,
        }"
      >
        {{ sectionDisplayName }}
      </span>
      <component
        :is="component.hydration && !disableLazyHydrate ? BaseLazyHydrate : BaseWrapper"
        v-for="(component, i) in components"
        :key="component.id"
        :when="component.hydration"
        v-bind="component.hydrationOptions"
      >
        <NuxtErrorBoundary @error="logError($event as Error, component)">
          <template v-if="isPreview" #error>
            <div
              aria-atomic="true"
              aria-live="assertive"
              class="my-4 min-h-24 f-col center gap-1 b rounded p-6 text-center text-base lh-normal shadow-xl md:text-lg"
              data-test-id="cms-preview-error-boundary"
              role="alert"
              style="background-color: #fefce8; border-color: #facc15; color: #713f12;"
            >
              <vf-icon class="mb-2" name="help" size="lg" style="color: #a16207;" />
              <span class="fw-bold">There is an error in this component</span>
              <span class="mt-1">It cannot be rendered. Check the <span class="fw-bold">lightbulb</span> in CMS and update the component.</span>
            </div>
          </template>
          <div
            v-style="{
              display: getVisibilityStyles(component.hideOnBreakpoint),
              mb: hasMargin(component),
              top: component.isSticky ? {
                sm: pxToRem(header.sticky.margin),
                md: pxToRem(header.sticky.margin),
              } : {},
            }"
            :class="[
              {
                [component.insetClasses]: hasInset(component),
                'sticky z-3 overflow-y-auto': component.isSticky,
                'h-full': fullHeight,
              },
              classWrapper,
            ]"
            :data-placement="`cms-${section.name}-${i + 1}`"
            :data-test-id="`cms-${component.componentType}-${component.id}`"
            :data-tracking-id="component.delocalizedTracking"
            :style="{
              maxHeight: component.isSticky ? `calc(100vh - ${pxToRem(header.sticky.margin)})` : undefined,
            }"
            v-bind="component.dataTestVariant ? { 'data-test-variant': component.dataTestVariant } : {}"
          >
            <cms-section-position-provider :index="i">
              <component
                :is="`lazy-cms-${component.componentType}`"
                :class="{ 'h-full': fullHeight }"
                :content="component"
                :data-cm-metadata="componentMetadata(component.id)"
                :first-element="pageContext && firstSection && i === 0"
                :full-height
                :page-context
              />
            </cms-section-position-provider>
          </div>
        </NuxtErrorBoundary>
      </component>
    </base-wrapper>
  </div>
</template>

<script lang="ts" setup>
import type { CSSClass, Responsive } from '#types/common'
import type { InsetContent } from '#types/content'
import type { HydrationMode } from '#types/hydration'
import type { Section } from '#types/page'
import { BaseLazyHydrate, BaseWrapper } from '#components'
import { SectionContextKey } from '#content/contexts/section'
import mappings from '#content/mappings'
import eSpotMapping from '#core/utils/cms/eSpotsMapping.json'

type ContentItem = {
  componentType: string
  hydration: HydrationMode
  hydrationOptions?: {
    observerOptions: IntersectionObserverInit
  }
  isSticky: boolean
  bottomMargins: Record<keyof Responsive, string>
} & InsetContent

const props = defineProps<{
  /**
   * CMS Section with content components
   */
  section: Section | any
  /**
   * Use page context in case you need CMS controlled layout to be applied (container) and bottom spacing
   */
  pageContext?: boolean
  /**
   * Lazy load media objects like images and videos inside section
   * Use for elements that would not be visible on the first screen
   */
  lazyMedia?: boolean
  disableLazyHydrate?: boolean
  fullHeight?: boolean
  firstSection?: boolean
  classWrapper?: CSSClass
}>()

const sectionData = {
  fullHeight: props.fullHeight,
  lazy: props.section.lazy || props.lazyMedia,
  name: props.section.name,
  section: props.section,
  fetchpriority: (props.section.lazy || props.lazyMedia) ? undefined : 'high' as const
}

const ref = useState('sectionRefs', () => ({}))
const setEspotRef = (el: Element | ComponentPublicInstance | null) => {
  if (props.section.name) ref.value[props.section.name] = el
}

const injectedData = inject(SectionContextKey, null)

if (injectedData) {
  provide(SectionContextKey, {
    ...sectionData,
    parent: injectedData
  })
}
else {
  provide(SectionContextKey, sectionData)
}

const { isPreview, isEspot } = useCms()
const { ds, components: { cms } } = useAppConfig()
const header = useHeaderStore()

const {
  componentSpacingMap,
  componentsWithoutContainer,
  componentTypesWithoutContainer,
  componentsWithoutMargin
} = cms.section
const { breakpoints } = ds

const components = computed<ContentItem[]>(() => (props.section.items || []).reduce((acc, item) => {
  const config = resolveContentComponent(item, mappings)

  if (!config) return acc

  return [
    ...acc,
    {
      ...config,
      bottomMargins: item.bottomMargins
        ? Object.keys(breakpoints).reduce((acc, bp) => ({
            ...acc,
            [bp]: componentSpacingMap[item.bottomMargins?.[bp] || 'default']
          }), {})
        : {},
      ...(!props.pageContext && { componentSidePaddings: undefined, componentMaxWidth: undefined }),
      insetClasses: getContentInset(item, cms.section)
    }
  ]
}, [] as ContentItem[]))

const logError = (e: Error, { componentType, id }: ContentItem) => {
  log.error(`${e?.message} | CMS Error: component - ${componentType}, id - ${id}`, { stackTrace: e.stack })
}

const placementMetadata = isPreview
  ? JSON.stringify([
      { _: `properties.placement-${props.section.name}` },
      { placementRequest: [{ isInLayout: true, hasItems: true, placementName: props.section.name }] }
    ])
  : undefined

const componentMetadata = (id: string) => isPreview ? `[{"_":{"$Ref":"content/${id}"}}]` : undefined

const hasInset = ({ componentType, type }: ContentItem): boolean =>
  props.pageContext
  && !componentsWithoutContainer?.includes(type)
  && !componentTypesWithoutContainer?.includes(componentType)

const hasMargin = ({ type, bottomMargins }: ContentItem) =>
  !componentsWithoutMargin?.includes(type)
  && (props.pageContext || components.value.length > 1)
    ? bottomMargins
    : null

const sectionDisplayName = eSpotMapping[props.section.name]
const selectedEspot = useState<string | null>('espotSelected', () => null)

const highlight = computed(() => isEspot.value && !!sectionDisplayName)
const isSelectedEspot = computed(() => selectedEspot.value === props.section.name)
const highlightBorderColor = computed(() => (isSelectedEspot.value ? '#ef4444' : '#60a5fa'))
const highlightBackgroundColor = computed(() => (isSelectedEspot.value ? '#fee2e2' : '#dcebff'))
</script>
